import { useFetch } from "./utils";
import { useEffect, useState } from "react";
import { visionURL } from "../config/environment";
import visionCallService from "../services/visionCallService";
import JsonApiService from "../services/jsonapiService";
import { getRoleIds } from "../utils/roles";
import Jsona from "jsona";

const dataFormatter = new Jsona();
const include = ["position", "location", "customer"];

const employeesApiService = async (filter = {}, sort = "", page = {}) =>
  new JsonApiService("users", {
    baseUrl: `${visionURL}/admin_console/api/administration/employees`,
    credentials: "include",
    filter: filter,
    sort: sort,
    page
  });

export const useEmployees = () => {
  return {
    getEmployees: async ({ sortBy, page, filters }) => {
      const service = await employeesApiService();
      const response = await service
        .page(page)
        .sort(sortBy)
        .include(include)
        .where(filters)
        .all()
        .then(response => response.json());
      return {
        data: dataFormatter.deserialize(response),
        ...response.meta
      };
    }
  };
};

export const useEmployee = employeeId => {
  const [isLoading, setIsLoading] = useState(false);
  const [employee, setEmployee] = useState(null);

  useEffect(() => {
    const fetchEmployee = async () => {
      if (employeeId) {
        setIsLoading(true);
        const service = await employeesApiService();
        const response = await service
          .find(employeeId)
          .then(response => response.json());
        setIsLoading(false);
        setEmployee(dataFormatter.deserialize(response));
      }
    };
    fetchEmployee();
  }, [employeeId]);

  return {
    employee,
    isLoading
  };
};

export const useUserAverages = (userId, trainingObjectType) => {
  const [isLoading, setIsLoading] = useState(false);
  const [userAverages, setUserAverages] = useState(null);

  useEffect(() => {
    const fetchUserAverages = async () => {
      if (userId) {
        setIsLoading(true);
        const service = await employeesApiService();
        const response = await service
          .customParams({ training_object_type: trainingObjectType })
          .related(userId, "averages")
          .then(response => response.json());
        setIsLoading(false);
        setUserAverages(response);
      }
    };
    fetchUserAverages();
  }, [userId, trainingObjectType]);

  return {
    userAverages,
    isLoading
  };
};

const normalizeRoles = roles => {
  const roleIds = getRoleIds(roles);
  let normalizedRoles = {};
  const manageTeamIndex = roles.findIndex(
    role => role.id === roleIds.manageEmployees
  );
  if (manageTeamIndex != -1) roles[manageTeamIndex].name = "Manage Team";
  roles.map(role => (normalizedRoles[role.id] = role));
  return normalizedRoles;
};

export const useUserAssignableRoles = employeeID => {
  const apiUrl = `${visionURL}/admin_console/api/administration/employees/users/${employeeID}/roles`;
  const roles = useFetch(apiUrl);
  return roles ? normalizeRoles(roles) : roles;
};

const userRoleApiService = async () =>
  new JsonApiService("users", {
    baseUrl: `${visionURL}/admin_console/api/administration/employees`,
    credentials: "include"
  });

export const useUpdateRoles = (employeeID, roles) => {
  const saveUserRoles = async () => {
    let parsedRoles = { roles: {} };
    Object.entries(roles).map(
      ([id, role]) => (parsedRoles["roles"][id] = role.status)
    );

    const service = await userRoleApiService();
    const json = await service
      .update(
        employeeID,
        visionCallService.serializeRequestBody(
          { id: employeeID, ...parsedRoles },
          "user"
        )
      )
      .then(response => response.json())
      .catch(error => error);
    return json;
  };
  return saveUserRoles;
};

export const usePasswordReset = employeeID => {
  const sendPasswordReset = async () => {
    const apiUrl = `${visionURL}/admin_console/api/administration/employees/users/${employeeID}/reset_password`;
    const status = await visionCallService.postData(
      {},
      apiUrl,
      res => res,
      err => console.error(err)
    );
    return status;
  };
  return sendPasswordReset;
};

export const useUserTrainingRecords = () => {
  return {
    getUserTrainingRecords: async ({ employeeId, page, sort, filters }) => {
      const service = await userRoleApiService();
      const response = await service
        .page(page)
        .sort(sort)
        .where(filters)
        .include([
          "training_object",
          "homework_submissions.homework",
          "brsp_submissions"
        ])
        .related(employeeId, "training_records")
        .then(response => response.json());
      if (response.error) {
        return {
          error: response.error
        };
      }
      return {
        data: dataFormatter.deserialize(response),
        ...response.meta,
        page: response.meta.page - 1
      };
    }
  };
};

export const createTrainingRecord = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  return {
    createOtherTrainingRecord: async (data, employeeId) => {
      setIsSubmitting(true);
      const service = await userRoleApiService();
      const response = await service
        .createRelated(employeeId, "training_records", JSON.stringify(data))
        .then(response => response.json());
      const submissionData = dataFormatter.deserialize(response);
      setIsSubmitting(false);
      return submissionData;
    },
    isSubmitting
  };
};

export const updateTrainingRecord = () => {
  const [isUpdating, setIsUpdating] = useState(false);
  return {
    updateOtherTrainingRecord: async (employeeId, recordId, newRecordData) => {
      setIsUpdating(true);
      const service = await userRoleApiService();
      const response = await service
        .updateRelatedResource(
          employeeId,
          "training_records",
          recordId,
          JSON.stringify(newRecordData)
        )
        .then(response => response.json());
      setIsUpdating(false);
      return dataFormatter.deserialize(response);
    },
    isUpdating
  };
};

export const useTrainingRecordProgress = () => {
  const [loading, setLoading] = useState(false);
  return {
    getTrainingRecordProgress: async (employeeId, recordId) => {
      setLoading(true);
      const service = await userRoleApiService();
      const response = await service
        .relatedResource(employeeId, "training_records", recordId)
        .then(response => response.json());
      setLoading(false);
      return dataFormatter.deserialize(response);
    },
    loading
  };
};
